import { UserRoundCheck } from 'lucide-react';

export type Classification = 'adult' | 'youth';

export interface Participant {
  id: string;
  name: string;
  classification: Classification;
  /** Shown in the participant strip only, never in the message stream. */
  role?: string;
}

/**
 * Youth/adult status is carried by **shape and text, never colour** (spec §6, §10).
 *
 *   adult — circular avatar, solid 1.5px ink border, radius-full, user-round-check
 *   youth — rounded-square avatar, dashed 1.5px strong border, 8px radius, no icon
 *
 * There is deliberately no colour prop and no badge variant. A youth chip must
 * never gain a red or amber tint, and the word "minor" appears nowhere.
 */
export function ParticipantChip({ participant }: { participant: Participant }) {
  const isAdult = participant.classification === 'adult';
  const initials = participant.name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

  return (
    <span
      className="inline-flex items-center gap-2 t-caption"
      style={{
        minHeight: '34px',
        padding: '0 var(--space-3) 0 var(--space-1)',
        color: 'var(--color-text-primary)',
        borderRadius: isAdult ? 'var(--radius-full)' : '8px',
        border: isAdult
          ? '1.5px solid var(--color-brand-ink)'
          : '1.5px dashed var(--color-border-strong)',
      }}
    >
      <Avatar initials={initials} classification={participant.classification} />
      <span>{participant.name}</span>

      {/* The icon is the adult marker. Youth get no suffix — status is confirmable
          in this strip, not stamped on a child. */}
      {isAdult && <UserRoundCheck size={13} aria-hidden="true" />}

      {/* The shape difference is visual; this keeps it available to screen readers
          without printing a label next to a youth's name on screen. */}
      <span className="sr-only">{isAdult ? '(adult)' : '(youth)'}</span>
    </span>
  );
}

/**
 * "+N" in a plain outlined pill (spec §6).
 *
 * Plain on purpose: the two chip shapes already mean adult and youth, so an overflow chip that
 * borrowed either would be claiming something about the people it stands for. It says how many
 * are not shown and nothing else.
 */
export function OverflowChip({ count, onClick }: { count: number; onClick?: () => void }) {
  const label = `Show ${count} more`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex items-center t-caption"
      style={{
        minHeight: '34px',
        padding: '0 var(--space-3)',
        background: 'none',
        color: 'var(--color-text-secondary)',
        borderRadius: 'var(--radius-full)',
        border: '1.5px solid var(--color-border-strong)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      +{count}
    </button>
  );
}

export function Avatar({
  initials,
  classification,
  size = 26,
}: {
  initials: string;
  classification: Classification;
  size?: number;
}) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        fontWeight: 600,
        fontFamily: 'var(--font-ui)',
        background: 'var(--color-surface-sunken)',
        color: 'var(--color-text-secondary)',
        // The only difference: circle for adults, rounded square for youth.
        borderRadius: classification === 'adult' ? '50%' : 'var(--radius-sm)',
      }}
    >
      {initials}
    </span>
  );
}

/**
 * Mandatory wherever chips appear (spec §6, §10). Not optional, not collapsible,
 * not behind a tooltip — the shape language is only usable if it is decoded on
 * the same screen.
 */
export function ParticipantLegend() {
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2 t-caption"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      <span className="inline-flex items-center gap-2">
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: 'var(--radius-full)',
            border: '1.5px solid var(--color-brand-ink)',
          }}
        />
        Round outline &middot; adult
      </span>

      <span className="inline-flex items-center gap-2">
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: '5px',
            border: '1.5px dashed var(--color-border-strong)',
          }}
        />
        Square outline &middot; youth
      </span>
    </div>
  );
}
